// App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { AuthProvider, useAuth } from './Auth/AuthContext';
import { GlobalProvider } from './Context/GlobalContext';
import { AIModelsProvider } from './Context/AIModelsContext';
import { AffiliateProvider } from './Affiliate/AffiliateContext';
import { WorkspaceProvider, useWorkspace } from './Context/WorkspaceContext';
import { UserProvider } from './Context/UserContext';
import { NotificationProvider } from './Context/NotificationContext';
import { ToastProvider } from './Context/ToastContext';
import ToastNotification from './Components/ToastNotification';
import ErrorBoundary from './ErrorBoundary';
import LoadingSpinner from './Components/LoadingSpinner';
import { OnboardingSetupProvider } from './Onboarding/OnboardingSetupContext';
import GlobalSetupModal from './Onboarding/components/GlobalSetupModal';
import { useVersionPolling } from './hooks/useVersionPolling';
import { useGrandfatheredAccess } from './hooks/useGrandfatheredAccess';
import AgentChats from './Dashboard/SupportBV/AgentChats';
import AgentLeads from './Dashboard/SupportBV/AgentLeads';
import WorkspaceTraining from './Dashboard/Training/WorkspaceTraining';
import AgentAnalytics from './Dashboard/SupportBV/AgentAnalytics';
import GeneralSettings from './Dashboard/SupportBV/Settings/GeneralSettings';
import RateLimitingSettings from './Dashboard/SupportBV/Settings/RateLimitingSettings';
import VoiceRateLimitingSettings from './Dashboard/SupportBV/Settings/VoiceRateLimitingSettings';
import BrandSettings from './Dashboard/Branding/BrandSettings';
import SubscriptionExpired from './pages/SubscriptionExpired';
import NotFound from './pages/NotFound';

// Helper function for lazy loading with retry on chunk load failure
// This handles the case when users have cached old JS that references chunks that no longer exist
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming chunk load failure, force refresh the page
        sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
        // Return a placeholder while reloading
        return { default: () => null };
      }
      // If already refreshed and still failing, throw the error
      throw error;
    }
  });

// Lazy-loaded components (with retry for chunk load failures)
const WorkspaceDashboard = lazyWithRetry(
  () => import('./Dashboard/WorkspaceDashboard')
);
const Login = lazyWithRetry(() => import('./Auth/Login'));
const Register = lazyWithRetry(() => import('./Auth/Register'));
const ForgotPassword = lazyWithRetry(() => import('./Auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./Auth/ResetPassword'));
const VerifyEmail = lazyWithRetry(() => import('./Auth/VerifyEmail'));
const AgentsList = lazyWithRetry(() => import('./Dashboard/SupportBV/AgentsList'));
const Integration = lazyWithRetry(() => import('./Dashboard/SupportBV/Integration'));
const Account = lazyWithRetry(() => import('./Dashboard/Account/Account'));
const JoinWorkspace = lazyWithRetry(() => import('./Auth/JoinWorkspace'));

// New Admin components
const AdminLogin = lazyWithRetry(() => import('./Admin/AdminLogin'));
const AdminDashboard = lazyWithRetry(() => import('./Admin/AdminDashboard'));
const BlogPostCreate = lazyWithRetry(() => import('./Admin/BlogPostCreate'));
const BlogPostEdit = lazyWithRetry(() => import('./Admin/BlogPostEdit'));

const ChatbotCustomization = lazyWithRetry(
  () => import('./Dashboard/SupportBV/ChatbotDash/ChatbotCustomization')
);
const ChatbotPersonality = lazyWithRetry(
  () => import('./Dashboard/SupportBV/ChatbotDash/ChatbotPersonality')
);
const AISettings = lazyWithRetry(() => import('./Dashboard/SupportBV/ChatbotDash/AISettings'));
const VoiceSettings = lazyWithRetry(() => import('./Dashboard/SupportBV/ChatbotDash/VoiceSettings'));
const ChatbotTest = lazyWithRetry(() => import('./Dashboard/SupportBV/ChatbotTest'));
const Onboarding = lazyWithRetry(() => import('./Onboarding/Onboarding'));

const ActionFlow = lazyWithRetry(() => import('./Dashboard/SupportBV/ActionFlow'));
const SocialMediaCreator = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/SocialMediaCreator'));
const SocialMediaMain = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/SocialMediaMain'));
const SocialMediaFolders = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/SocialMediaFolders'));
const SocialMediaFolderDetail = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/SocialMediaFolderDetail'));
// SchedulePosts removed - schedule is now inline in SocialMediaCreator
const XOAuthCallback = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/XOAuthCallback'));
const LinkedInOAuthCallback = lazyWithRetry(() => import('./Dashboard/SocialMediaBV/LinkedInOAuthCallback'));
const ImageGenerationCreator = lazyWithRetry(() => import('./Dashboard/ImageGenBV/ImageGenerationCreator'));
const ImageGenerationMain = lazyWithRetry(() => import('./Dashboard/ImageGenBV/ImageGenerationMain'));
const ImageGenerationFolders = lazyWithRetry(() => import('./Dashboard/ImageGenBV/ImageGenerationFolders'));
const ImageGenerationFolderDetail = lazyWithRetry(() => import('./Dashboard/ImageGenBV/ImageGenerationFolderDetail'));
const VideoGenerationCreator = lazyWithRetry(() => import('./Dashboard/VideoGenBV/VideoGenerationCreator'));
const VideoGenerationMain = lazyWithRetry(() => import('./Dashboard/VideoGenBV/VideoGenerationMain'));
const VideoGenerationFolders = lazyWithRetry(() => import('./Dashboard/VideoGenBV/VideoGenerationFolders'));
const VideoGenerationFolderDetail = lazyWithRetry(() => import('./Dashboard/VideoGenBV/VideoGenerationFolderDetail'));
const SEOAnalysisCreator = lazyWithRetry(() => import('./Dashboard/SeoBV/SEOAnalysisCreator'));
const SEOAnalysisMain = lazyWithRetry(() => import('./Dashboard/SeoBV/SEOAnalysisMain'));
const SEOAnalysisResults = lazyWithRetry(() => import('./Dashboard/SeoBV/SEOAnalysisResults'));
const LeadMain = lazyWithRetry(() => import('./Dashboard/LeadBV/LeadMain'));
const LeadSearch = lazyWithRetry(() => import('./Dashboard/LeadBV/LeadSearch'));

// Component to handle training redirect while preserving query parameters
const TrainingRedirect = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  // Check for OAuth redirects and route to appropriate section
  const isGoogleOAuth = query.get('google_connected') === 'true' || query.get('google_error') === 'true';
  const isNotionOAuth = query.get('notion_connected') === 'true' || query.get('notion_error') === 'true';

  if (isGoogleOAuth) {
    return <Navigate to={`googledocs${location.search}`} replace />;
  } else if (isNotionOAuth) {
    return <Navigate to={`notion${location.search}`} replace />;
  }

  // Default to website section
  return <Navigate to={`website${location.search}`} replace />;
};

const PublicRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  return isLoggedIn ? (
    <Navigate to='/' state={{ from: location }} replace />
  ) : (
    children
  );
};

const PrivateRoute = ({ children }) => {
  const { isLoggedIn, needsOnboarding, isLoading } = useAuth();
  const { needsSubscriptionExpiredRedirect } = useWorkspace();
  const location = useLocation();

  // Wait for loading state to resolve
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not logged in, redirect to login
  if (!isLoggedIn) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // If logged in BUT needs onboarding
  if (needsOnboarding === true) {
    // If they are trying to access the onboarding page, allow it
    if (location.pathname === '/onboarding') {
      return children;
    }
    // Otherwise, redirect them TO the onboarding page
    return <Navigate to='/onboarding' state={{ from: location }} replace />;
  }

  // If logged in BUT needs subscription expired redirect (no active workspace creator subscription)
  // Allow access to account page so users can renew their subscriptions
  if (needsSubscriptionExpiredRedirect && location.pathname !== '/subscription-expired' && !location.pathname.startsWith('/account')) {
    return <Navigate to='/subscription-expired' state={{ from: location }} replace />;
  }

  // In all other cases (e.g. logged in and doesn't need onboarding),
  // allow access to the requested private route
  return children;
};

// New route for admin access
const AdminRoute = ({ children }) => {
  const { isAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAdmin ? (
    children
  ) : (
    <Navigate to='/admin/login' state={{ from: location }} replace />
  );
};

// Route wrapper for grandfathered features (SEO BV, Lead BV)
const GrandfatheredRoute = ({ children, feature }) => {
  const { hasGrandfatheredAccess, isLoading } = useGrandfatheredAccess();

  // Wait for context data to load
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Check if user has access to this grandfathered feature
  if (!hasGrandfatheredAccess(feature)) {
    return <NotFound />;
  }

  return children;
};

const AuthInitializer = ({ children }) => {
  const { isLoading } = useAuth();
  const location = useLocation();

  // Don't show loading spinner on onboarding page - let onboarding handle its own loading
  if (location.pathname === '/onboarding') {
    return children;
  }

  return isLoading ? <LoadingSpinner /> : children;
};

// Route-based preloader component
const RouteBasedPreloader = () => {
  const location = useLocation();
  const { fetchWorkspaceDetails, currentWorkspace, isUsingCachedData } = useWorkspace();

  useEffect(() => {
    // Parse URL to determine if we need to preload specific workspace data
    const path = location.pathname;
    const agentMatch = path.match(/\/agent\/([^/]+)/);

    if (agentMatch && currentWorkspace?.workspace_id) {
      // If we're on an agent route and workspace data might be stale or missing
      if (isUsingCachedData) {
        // Preload fresh workspace data in background
        fetchWorkspaceDetails(currentWorkspace.workspace_id, false);
      }
    }
  }, [location.pathname, currentWorkspace?.workspace_id, fetchWorkspaceDetails, isUsingCachedData]);

  return null; // This component only handles preloading
};

const AppContent = () => {
  const location = useLocation();

  // Auto-refresh when new deployment is detected
  useVersionPolling();

  // Track page views when location changes, but only in test/production
  useEffect(() => {
    const currentEnv = process.env.REACT_APP_DEV;
    if (currentEnv === 'TEST' || currentEnv === 'PROD') {
      ReactGA.send({ hitType: "pageview", page: location.pathname });
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <RouteBasedPreloader />
      <ToastNotification />
      <GlobalSetupModal />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes location={location} key={location.key}>
          {/* Onboarding Route - Keep separate */}
          <Route
            path='/onboarding'
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />

          {/* Root redirect to training/website */}
          <Route path='/' element={<Navigate to='/training/website' replace />} />

          {/* Agents route shows AgentsList */}
          <Route
            path='/agents'
            element={
              <PrivateRoute>
                <WorkspaceDashboard />
              </PrivateRoute>
            }
          >
            <Route index element={<AgentsList />} />
          </Route>

          {/* Main Dashboard Container */}
          <Route
            path='/'
            element={
              <PrivateRoute>
                <WorkspaceDashboard />
              </PrivateRoute>
            }
          >

            {/* Workspace Training */}
            <Route path='training' element={<TrainingRedirect />} />
            <Route path='training/:section' element={<WorkspaceTraining />} />

            {/* Brand Settings */}
            <Route path='brand-settings' element={<Navigate to='basics' replace />} />
            <Route path='brand-settings/:section' element={<BrandSettings />} />

            {/* Social Media Routes */}
            <Route path='social-media' element={<SocialMediaMain />} />
            <Route path='social-media/folders' element={<SocialMediaFolders />} />
            <Route path='social-media/folders/:folderId' element={<SocialMediaFolderDetail />} />
            <Route path='social-media-creator/:postId' element={<SocialMediaCreator />} />
            <Route path='social-media-creator' element={<SocialMediaCreator />} />
            <Route path='social-media/schedule-posts' element={<Navigate to='/social-media' replace />} />
            <Route path='social-media/x-callback' element={<XOAuthCallback />} />
            <Route path='social-media/linkedin-callback' element={<LinkedInOAuthCallback />} />

            {/* Image Generation Routes */}
            <Route path='image-generation' element={<ImageGenerationMain />} />
            <Route path='image-generation/folders' element={<ImageGenerationFolders />} />
            <Route path='image-generation/folders/:folderId' element={<ImageGenerationFolderDetail />} />
            <Route path='image-generation-creator/:imageId' element={<ImageGenerationCreator />} />
            <Route path='image-generation-creator' element={<ImageGenerationCreator />} />
            {/* Video Generation Routes */}
            <Route path='video-generation' element={<VideoGenerationMain />} />
            <Route path='video-generation/folders' element={<VideoGenerationFolders />} />
            <Route path='video-generation/folders/:folderId' element={<VideoGenerationFolderDetail />} />
            <Route path='video-generation-creator/:videoId' element={<VideoGenerationCreator />} />
            <Route path='video-generation-creator' element={<VideoGenerationCreator />} />
            {/* SEO Analysis Routes - Grandfathered Access */}
            <Route path='seo-analysis' element={<GrandfatheredRoute feature="seoBV"><SEOAnalysisMain /></GrandfatheredRoute>} />
            <Route path='seo-analysis-creator' element={<GrandfatheredRoute feature="seoBV"><SEOAnalysisCreator /></GrandfatheredRoute>} />
            <Route path='seo-analysis-results/:analysisId' element={<GrandfatheredRoute feature="seoBV"><SEOAnalysisResults /></GrandfatheredRoute>} />
            <Route path='seo-analysis-results' element={<GrandfatheredRoute feature="seoBV"><SEOAnalysisResults /></GrandfatheredRoute>} />

            {/* Lead BV Routes - Grandfathered Access */}
            <Route path='lead-main' element={<GrandfatheredRoute feature="leadBV"><LeadMain /></GrandfatheredRoute>} />
            <Route path='lead-search/:searchId' element={<GrandfatheredRoute feature="leadBV"><LeadSearch /></GrandfatheredRoute>} />
            <Route path='lead-search' element={<GrandfatheredRoute feature="leadBV"><LeadSearch /></GrandfatheredRoute>} />

            {/* Account */}
            <Route path='account' element={<Account />} />

            {/* Agent specific routes */}
            <Route path='agent/:id'>

              {/* Chatbot customization tabs */}
              <Route path='chatbot/appearance' element={<ChatbotCustomization />} />
              <Route path='chatbot/personality' element={<ChatbotPersonality />} />
              <Route path='chatbot/ai-settings' element={<AISettings />} />
              <Route path='chatbot/voice-settings' element={<VoiceSettings />} />

              <Route path='test' element={<ChatbotTest />} />
              <Route path='analytics' element={<AgentAnalytics />} />
              <Route path='chats' element={<AgentChats />} />
              <Route path='leads' element={<AgentLeads />} />
              <Route path='integration' element={<Integration />} />
              <Route path='integration/:section' element={<Integration />} />
              <Route path='settings' element={<Navigate to='general' replace />} />
              <Route path='settings/general' element={<GeneralSettings />} />
              <Route path='settings/rate-limiting' element={<RateLimitingSettings />} />
              <Route path='settings/voice-rate-limiting' element={<VoiceRateLimitingSettings />} />
              <Route path='action-flow' element={<ActionFlow />} />
            </Route>
          </Route>

          {/* Admin routes */}
          <Route path='/admin/login' element={<AdminLogin />} />

          <Route
            path='/admin'
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route
            path='/admin/dashboard'
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route
            path='/admin/blog/create'
            element={
              <AdminRoute>
                <BlogPostCreate />
              </AdminRoute>
            }
          />

          <Route
            path='/admin/blog/edit/:postId'
            element={
              <AdminRoute>
                <BlogPostEdit />
              </AdminRoute>
            }
          />

          <Route
            path='/login'
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path='/register'
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path='/verify-email'
            element={
              <PublicRoute>
                <VerifyEmail />
              </PublicRoute>
            }
          />
          <Route
            path='/forgot-password'
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path='/reset-password'
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          <Route path='/join-workspace/:invitationId' element={<JoinWorkspace />} />

          {/* WhatsApp callback route - this should be public */}
          {/* Removed as per edit hint */}

          <Route
            path='/subscription-expired'
            element={
              <PrivateRoute>
                <SubscriptionExpired />
              </PrivateRoute>
            }
          />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AffiliateProvider>
        <GlobalProvider>
          <AuthProvider>
            <UserProvider>
              <AIModelsProvider>
                <WorkspaceProvider>
                  <ToastProvider>
                    <NotificationProvider>
                      <OnboardingSetupProvider>
                        <AuthInitializer>
                          <AppContent />
                        </AuthInitializer>
                      </OnboardingSetupProvider>
                    </NotificationProvider>
                  </ToastProvider>
                </WorkspaceProvider>
              </AIModelsProvider>
            </UserProvider>
          </AuthProvider>
        </GlobalProvider>
      </AffiliateProvider>
    </ErrorBoundary>
  );
};

export default App;
